import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, MapPin, ShieldCheck, Phone, FileText, XCircle, AlertTriangle } from "lucide-react";
import { POSITION_NAMES } from "@/lib/constants";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function fileUrl(url) {
  if (!url) return "#";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function statusClass(status) {
  if (status === "approved") return "bg-green-100 text-green-700";
  if (status === "rejected") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

export default function DispatcherProfessionals() {
  const [pros, setPros] = useState([]);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState("all");
  const [availOnly, setAvailOnly] = useState(false);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [checks, setChecks] = useState({});
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/professionals");
    setPros(data);
  };

  useEffect(() => { load(); }, []);

  const setCheck = (credentialId, key, value) => {
    setChecks((prev) => ({
      ...prev,
      [credentialId]: {
        ...(prev[credentialId] || {}),
        [key]: value,
      },
    }));
  };

  const reviewCredential = async (professionalId, credentialId, status) => {
    const verification_checks = checks[credentialId] || {};

    if (status === "approved") {
      const required = [
        "license_number_cross_checked",
        "government_registry_verified",
        "license_active",
        "expiry_date_verified",
        "name_matches_profile",
      ];

      const missing = required.filter((k) => !verification_checks[k]);
      if (missing.length > 0) {
        toast.error("Complete all license verification checklist items before approval.");
        return;
      }
    }

    let rejection_reason = null;
    if (status === "rejected") {
      rejection_reason = window.prompt("Reason for rejection:");
      if (!rejection_reason) return;
    }

    const verification_notes = window.prompt("Verification notes, optional:") || "";

    try {
      await api.patch(`/professionals/${professionalId}/credentials/${credentialId}/review`, {
        status,
        rejection_reason,
        verification_checks,
        verification_notes,
      });
      toast.success(`Credential ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed");
    }
  };

  const filtered = pros.filter((p) => {
    const matchQ = `${p.first_name} ${p.last_name} ${p.primary_position}`.toLowerCase().includes(q.toLowerCase());
    const matchPos = pos === "all" || p.primary_position === pos;
    const matchAvail = !availOnly || p.available_now;
    const hasPending = (p.credentials || []).some((c) => (c.status || "pending") === "pending");
    const matchReview = !reviewOnly || hasPending || p.verification_status !== "approved";
    return matchQ && matchPos && matchAvail && matchReview;
  });

  const pendingCount = pros.reduce((sum, p) => sum + (p.credentials || []).filter((c) => (c.status || "pending") === "pending").length, 0);

  return (
    <div className="space-y-6" data-testid="dispatcher-professionals">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Professional Network</h1>
        <div className="px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold">
          {pendingCount} credential{pendingCount === 1 ? "" : "s"} pending review
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex gap-3 flex-wrap">
          <Input placeholder="Search by name or position…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="pros-search" />
          <Select value={pos} onValueChange={setPos}>
            <SelectTrigger className="w-48" data-testid="pros-position-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All positions</SelectItem>
              {POSITION_NAMES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            onClick={() => setAvailOnly(!availOnly)}
            data-testid="pros-avail-toggle"
            className={`px-4 py-2 rounded-lg text-sm font-semibold border ${availOnly ? "bg-brand-teal text-white border-brand-teal" : "bg-white text-slate-700 border-slate-300"}`}
          >
            Available Now Only
          </button>
          <button
            onClick={() => setReviewOnly(!reviewOnly)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border ${reviewOnly ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-700 border-slate-300"}`}
          >
            Credential Review Only
          </button>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((p) => {
          const verificationStatus = p.verification_status || "pending_documents";
          const approved = verificationStatus === "approved";

          return (
            <Card
              key={p.id}
              onClick={() => navigate(`/dispatch/professionals/${p.id}`)}
              className={`transition-colors cursor-pointer hover:shadow-lg ${approved ? "hover:border-brand-teal" : "border-amber-200"}`}
              data-testid={`pro-card-${p.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-brand-navy text-white flex items-center justify-center font-bold text-lg">
                    {p.first_name?.[0]}{p.last_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">{p.first_name} {p.last_name}</div>
                    <div className="text-xs text-slate-500">{p.primary_position}</div>
                  </div>
                  {approved ? (
                    <span className="status-pill bg-green-100 text-green-700 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />Approved</span>
                  ) : (
                    <span className="status-pill bg-amber-100 text-amber-700 text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" />Review</span>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div><div className="text-slate-500">NetStaff</div><div className="font-bold text-brand-navy flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" />{p.netstaff_score}</div></div>
                  <div><div className="text-slate-500">Rate/hr</div><div className="font-bold text-brand-navy">${p.hourly_rate}</div></div>
                  <div><div className="text-slate-500">Exp</div><div className="font-bold text-brand-navy">{p.experience_years} yrs</div></div>
                </div>

                <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}</span>
                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>
                </div>

                <div className="mt-3 text-xs">
                  <span className={`status-pill ${statusClass(approved ? "approved" : verificationStatus === "rejected" ? "rejected" : "pending")}`}>
                    Verification: {verificationStatus.replaceAll("_", " ")}
                  </span>
                </div>

                {p.specialties?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.specialties.slice(0, 3).map((s) => (
                      <span key={s} className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{s}</span>
                    ))}
                  </div>
                )}

                <div className="mt-4 border-t border-slate-100 pt-4">
                  <div className="text-sm font-semibold text-slate-900 mb-2">Credentials</div>

                  {(p.credentials || []).length === 0 && (
                    <div className="text-xs text-slate-500">No credentials uploaded.</div>
                  )}

                  <div className="space-y-2">
                    {(p.credentials || []).map((c) => (
                      <div key={c.id} className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-medium text-slate-900">{c.type}</div>
                            <div className="text-xs text-slate-500">
                              {c.expiry ? `Expires ${c.expiry}` : "No expiry"} · {c.uploaded_at ? c.uploaded_at.slice(0, 10) : "—"}
                            </div>
                            {c.rejection_reason && (
                              <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                                <XCircle className="w-3 h-3" /> {c.rejection_reason}
                              </div>
                            )}
                          </div>
                          <span className={`status-pill ${statusClass(c.status || "pending")}`}>{c.status || "pending"}</span>
                        </div>

                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {c.file_url && (
                            <a onClick={(e) => e.stopPropagation()} href={fileUrl(c.file_url)} target="_blank" rel="noreferrer" className="text-xs text-brand-navy hover:underline flex items-center gap-1">
                              <FileText className="w-3 h-3" /> View File
                            </a>
                          )}
                          {(c.status || "pending") === "pending" && (
                            <div className="w-full mt-3 space-y-3">
                              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                <div className="text-xs font-semibold text-amber-800 mb-2">
                                  Dispatcher License Verification Checklist
                                </div>
                                <div className="space-y-2 text-xs text-slate-700">
                                  {[
                                    ["license_number_cross_checked", "License number cross-checked on official government / regulator website"],
                                    ["government_registry_verified", "Professional found on official government / regulatory registry"],
                                    ["license_active", "License is active and in good standing"],
                                    ["expiry_date_verified", "Expiry date is accurate and current"],
                                    ["name_matches_profile", "Registered name matches NetStaff profile"],
                                  ].map(([key, label]) => (
                                    <label key={key} className="flex items-start gap-2">
                                      <input
                                        type="checkbox"
                                        className="mt-0.5"
                                        checked={Boolean((checks[c.id] || {})[key])}
                                        onChange={(e) => setCheck(c.id, key, e.target.checked)}
                                      />
                                      <span>{label}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 flex-wrap">
                                <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-xs" onClick={(e) => { e.stopPropagation(); reviewCredential(p.id, c.id, "approved"); }}>
                                  Approve
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 border-red-300 text-red-700 hover:bg-red-50 text-xs" onClick={(e) => { e.stopPropagation(); reviewCredential(p.id, c.id, "rejected"); }}>
                                  Reject
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
